import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { Formik } from 'formik';
import { isEqual } from 'lodash';
import { Constants, USER_ROLES } from '@pwc/pwc-deals-web-common';

import { useSyncedInfo } from 'common/components/Autosync/SyncedInfoProvider';
import { useLiveReportPreview } from '@pwc/pwc-deals-report-ui/build/components/ReportPreview/provider';
import featureFlags, {
  isLiveReportPreviewEnabled,
} from 'common/helpers/featureFlags';

import { useEventHub } from '@pwc/event-hub-ui';
import ResponsiveLoader from '../../../common/components/ResponsiveLoader/ResponsiveLoader';
import { useSections } from '../../../common/components/SectionLayout/SectionContext';
import { ENTITY_TYPES } from '../../../common/constants/General';
import useIsMountedRef from '../../../common/hooks/useIsMountedRef';
import useTabletOrMobile from '../../../common/hooks/useTabletOrMobile';
import useVersionToggle from '../../../common/hooks/useVersionToggle';
import useCustomCompareEffect from '../../../common/hooks/useCustomCompareEffect.ts';
import {
  hypothesesListPath,
  reportPreviewPath,
  viewHypothesisPath,
} from '../../../common/services/urlService';
import { SECTION_STATE_ACTION } from '../../analyses/Freeform/FreeformAnalysisForm/FreeformAnalysisFormUtil/sectionsReducer';
import { getLinksFromNarratives } from '../../narratives/narrativesUtilities';
import HypothesisForm from '../common/HypothesisForm';
import { hypothesisSchema } from '../common/hypothesisModel';
import { deleteHypothesis } from '../hypothesesService';
import {
  loadKfaVersions,
  resetHypothesis,
  selectClientVersion,
  selectDraftVersion,
  selectHypothesisVersions,
  selectedVersionStatus,
  setKfa,
  updateSelectionVersion,
  updateVersions,
} from '../hypothesisSlice';
import { putKfa } from '../kfaService';
import { initialSections, loadLibraryItems } from '../libraryItemsSupports';
import './EditHypothesis.scss';
import {
  showBannerError,
  showModalError,
} from '../../../common/redux/errors/errorsSlice';
import { getAlertConfigs } from '../../../common/components/Alert/AlertConfigs';
import { getConvertedHypotheses } from '../reportPreviewSupport.ts';
import {
  getLibraryItemsSelector,
  getReportPreviewPropsSelector,
  setReportPreviewProps,
  updateLibraryItem,
} from '../../analyses/libraryItemsSlice';
import { getTandCterritory } from '../../../common/helpers/projectInitUtil';
import { getProject, getProjectId } from '../../project/projectSlice';
import { isPowerPivotLibraryItem } from '../../analyses/common/LibraryItemCard/PowerPivot/constants/PowerPivotTypes';
import getLibraryItems from '../../liveReportPreview/utils';
import { getUserRole } from '../../../common/redux/security/loggedInUser/loggedInUserSlice';

// TODO: I will determine is client mode if so I will make a auto call
const EditHypothesis = ({
  currentlyVisibleConnections,
  errorType,
  gotoHypothesisView,
  isSaving,
  kfa,
  linkedNarrativesChanged,
  loadingThumbnail,
  preparedLibraryItems,
  projectId,
  rawKfa,
  refHandlerSaveForm,
  setConnectedContentChanged,
  setCurrentlyVisibleConnections,
  setErrorType,
  setIsSaving,
  thumbnail,
  updateThumbnail,
}) => {
  const dispatch = useDispatch();
  const isMountedRef = useIsMountedRef();
  const history = useHistory();
  const { hypothesisId: hypothesisContentThreadId } = useParams();
  const [{ currentSections = [] }, useSectionsDispatch] = useSections();
  const [loadingItems, setLoadingItems] = useState(true);
  const isRedirectingToViewModeRef = useRef(false);
  const draftVersion = useSelector(selectDraftVersion);
  const clientVersion = useSelector(selectClientVersion);
  const selectedVersions = useSelector(selectHypothesisVersions);
  const selectedVersion = useSelector(selectedVersionStatus);
  const [isSavingFulfilled, setIsSavingFulfilled] = useState(false);
  const [isSavingRejected, setIsSavingRejected] = useState(false);
  const { eventHub } = useEventHub();
  const { commentCounts } = useVersionToggle(
    isMountedRef.current,
    draftVersion,
    clientVersion
  );
  const userRole = useSelector(getUserRole);
  const isPwcUser = userRole === USER_ROLES.PWC_USER;

  const { libraryItems } = useSelector(getLibraryItemsSelector);
  const project = useSelector(getProject);

  const [isAutoSaveCalled, setAutoSaveCalled] = useState(false);
  const formRef = useRef();
  const contextStatusInfoItem = useSyncedInfo();

  const getPayload = () =>
    getConvertedHypotheses({
      pageId: '.kfa-detail-container',
      kfa: { ...formRef.current.values, tags: null, connectedAnalyses: null },
      sections: currentSections,
      thumbnail,
      libraryItems,
      territory: getTandCterritory(),
      project,
      isPwcUser,
    });

  const getCurrentData = useCallback(() => {
    const formValues = formRef.current?.values;
    if (formValues) {
      return {
        contentThreadId: kfa.contentThreadId,
        title: formValues.title,
        body: formValues.body,
        modifiedBy: formValues.modifiedBy,
        modifiedDate: formValues.dateModified,
        connections: { insights: [] },
        contentType: 'KFA',
        status:
          kfa.versionStatus === Constants.VERSION_STATUS.SHARED.value
            ? 'shared'
            : 'draft',
        libraryItems: { ...getLibraryItems(), ...libraryItems },
        sections: currentSections,
        thumbnail,
        insight: formValues.insight,
        insightItem: formValues.insightWithItems,
      };
    }
    return {};
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentSections,
    kfa.contentThreadId,
    kfa.versionStatus,
    thumbnail,
    libraryItems,
    formRef.current?.values,
  ]);

  const handleDelete = async (callback) => {
    try {
      await deleteHypothesis(projectId, hypothesisContentThreadId);
      dispatch(resetHypothesis());
      callback();
      history.push(hypothesesListPath(projectId));
    } catch (e) {
      callback();
      dispatch(
        showModalError(Constants.MoreMenuErrorType.UNABLE_TO_SAVE_CHANGE)
      );
    }
  };

  const x = useLiveReportPreview();
  x.getData = () => ({ payload: getPayload(), libraryItems });
  x.getPayload = getPayload;
  x.getProjectId = getProjectId;
  x.getReportPreviewPropsSelector = getReportPreviewPropsSelector;
  x.setReportPreviewProps = setReportPreviewProps;
  x.useTabletOrMobile = useTabletOrMobile;

  useEffect(() => {
    if (featureFlags.isVersioningPhase1Enabled) {
      if (!selectedVersion) {
        dispatch(
          updateSelectionVersion({
            contentId: draftVersion?.contentId || clientVersion?.contentId,
          })
        );
      }

      if (selectedVersions?.some(({ isSelected }) => isSelected)) {
        isRedirectingToViewModeRef.current = true;
        history.replace(
          viewHypothesisPath(projectId, hypothesisContentThreadId)
        );
      }
    }
  }, [
    clientVersion,
    dispatch,
    draftVersion,
    history,
    hypothesisContentThreadId,
    projectId,
    selectedVersion,
    selectedVersions,
    isRedirectingToViewModeRef,
  ]);

  useEffect(() => {
    dispatch(
      loadKfaVersions(projectId, hypothesisContentThreadId, false, isMountedRef)
    );
  }, [
    dispatch,
    projectId,
    isAutoSaveCalled,
    hypothesisContentThreadId,
    isMountedRef,
  ]);

  useEffect(() => {
    if (isLiveReportPreviewEnabled) {
      dispatch(
        setReportPreviewProps({
          currentData: getCurrentData(),
        })
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getCurrentData, setReportPreviewProps, dispatch]);

  // this is for a landing on the page so the initial data is correct
  useEffect(() => {
    if (isLiveReportPreviewEnabled) {
      dispatch(
        setReportPreviewProps({
          contentThreadId: kfa.contentThreadId,
          isShared: kfa.versionStatus === Constants.VERSION_STATUS.SHARED.value,
          territoryName: getTandCterritory(),
          projectName: project.name,
          currentData: getCurrentData(),
        })
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useCustomCompareEffect(
    // eslint-disable-next-line consistent-return
    () => {
      function dispatchExcelLibraryItems() {
        return (libItems) => {
          for (let i = 0; i < libItems?.length; i += 1) {
            const libItem = libItems[i];
            dispatch(
              updateLibraryItem({
                id: libItem.id,
                item:
                  libItem.type === 300 || libItem.type === 500
                    ? libItem.value
                    : libItem.value,
                libraryItemFormat: libItems.filter(
                  (ls) => ls.id === libItem.id
                ),
              })
            );
          }
        };
      }

      async function loadItems() {
        setLoadingItems(true);
        if (!rawKfa) {
          return;
        }

        const {
          libraryItemGroups,
          sharedLibraryItems,
          items,
          content: { sections },
        } = rawKfa;
        const loadedSections = loadLibraryItems({
          projectId,
          libraryItemGroups,
          sections,
          sharedLibraryItems: sharedLibraryItems || items,
          contextStatusInfoItem,
          excelLibraryItemsCallback: dispatchExcelLibraryItems(),
          ...preparedLibraryItems,
          caller: 'EditHypothesis',
        });

        const payload = loadedSections.length
          ? loadedSections
          : initialSections;
        // eslint-disable-next-line react-hooks/rules-of-hooks
        useSectionsDispatch({
          type: SECTION_STATE_ACTION.UPDATE_SECTIONS,
          payload,
        });
        setLoadingItems(false);
      }
      if (preparedLibraryItems.loaded) {
        loadItems();
      }
    },
    [
      rawKfa,
      projectId,
      isAutoSaveCalled,
      useSectionsDispatch,
      preparedLibraryItems,
    ],
    (
      [prevKFA, prevProjectId, _prevAutoSave, _prevDispatch, prevLibItems],
      [
        currentKFA,
        currentProjectId,
        _currentAutoSave,
        _currentDispatch,
        currentLibItems,
      ]
    ) => {
      return (
        isEqual(prevProjectId, currentProjectId) &&
        isEqual(currentLibItems, prevLibItems) &&
        isEqual(currentKFA, prevKFA)
      );
    }
  );

  const isOnlySharedAvailable =
    clientVersion && clientVersion.contentId && draftVersion === undefined;

  const hasCurrentlyVisibleConnections = !!currentlyVisibleConnections;

  useEffect(() => {
    if (
      isOnlySharedAvailable &&
      kfa &&
      formRef.current &&
      formRef.current.submitCount < 1 &&
      loadingItems === false &&
      hasCurrentlyVisibleConnections
    ) {
      formRef.current.submitForm();
    }
  }, [
    kfa,
    isOnlySharedAvailable,
    formRef,
    loadingItems,
    hasCurrentlyVisibleConnections,
  ]);

  useEffect(() => {
    return () => {
      if (featureFlags.isVersioningPhase1Enabled) {
        if (!isRedirectingToViewModeRef.current) {
          dispatch(setKfa(null));
          dispatch(updateVersions([]));
        }
      } else {
        dispatch(setKfa(null));
        dispatch(updateVersions([]));
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRedirectingToViewModeRef]);

  const handleRedirectToView = (autoSaveError) => {
    dispatch(resetHypothesis());
    if (autoSaveError) {
      history.replace(
        viewHypothesisPath(projectId, hypothesisContentThreadId),
        { autoSaveError }
      );
    } else {
      history.replace(viewHypothesisPath(projectId, hypothesisContentThreadId));
    }
  };

  const handleSave = async (values) => {
    try {
      setIsSaving(true);
      setIsSavingRejected(false);
      setIsSavingFulfilled(false);

      const links = getLinksFromNarratives(
        currentlyVisibleConnections,
        kfa.contentId
      );

      const fixItemData = (item) => {
        if (item.data && isPowerPivotLibraryItem(item.type)) {
          return JSON.stringify({ RichTextData: item.data });
        }
        if (
          item.data &&
          !item.data.includes('{"RichTextData"') &&
          item.type === Constants.LIBRARY_ITEM_TYPES.RICH_TEXT.value
        ) {
          return JSON.stringify({ RichTextData: item.data });
        }
        if (
          !item.data &&
          item.type === Constants.LIBRARY_ITEM_TYPES.RICH_TEXT.value
        ) {
          return JSON.stringify({ RichTextData: '' });
        }
        return item.data;
      };

      await putKfa(projectId, {
        contentThreadId: kfa.contentThreadId,
        contentId: draftVersion?.contentId || kfa.contentId,
        title: values.title,
        body: values.body,
        conclusion: values.conclusion,
        insight: {
          description: values.insight,
          order: 0,
          layoutType: 1,
          items: thumbnail
            ? [
                {
                  data: thumbnail.data,
                  id: thumbnail.id,
                  name: thumbnail.name,
                  status: thumbnail.status,
                  type: thumbnail.type,
                  order: 0,
                  childLibraryItems: [],
                },
              ]
            : [],
        },
        impact: values.impact,
        tags: values.tags.map((tag) => ({
          text: tag,
        })),
        links: [...links],
        sections: currentSections.map((section) => ({
          ...section,
          items: section.items.map((item) => ({
            data: fixItemData(item),
            id: item.id,
            name: item.name,
            status: item.status,
            type: item.type,
            column: item.analysisColumn,
            ChildLibraryItems: item.ChildLibraryItems
              ? // this is needed for newly ingested content
                item.ChildLibraryItems
              : // this is needed for previously ingested content
                item.childLibraryItems?.map((chItem) => chItem.id),
          })),
        })),
      });
      if (isOnlySharedAvailable) {
        setAutoSaveCalled(true);
        setIsSaving(false);
      }
      setErrorType();
      setIsSavingFulfilled(true);
      eventHub?.publish('comment-reload-comment-threads', {});
    } catch (e) {
      setErrorType(e);
      setIsSavingFulfilled(false);
      setIsSavingRejected(true);
      const errorMessage = getAlertConfigs(e);
      dispatch(showBannerError(errorMessage.message));
    } finally {
      setIsSaving(false);
    }
  };

  refHandlerSaveForm.current = () => {
    if (isSaving) {
      return undefined;
    }

    return formRef.current.submitForm();
  };

  const redirectToReportPreview = (version) => {
    history.replace(
      reportPreviewPath(
        projectId,
        hypothesisContentThreadId,
        version,
        true,
        ENTITY_TYPES.KFA
      )
    );
  };

  const previewAction = async (values) => {
    await handleSave(values);
    if (!errorType) {
      redirectToReportPreview('latest');
    }
  };

  if (isOnlySharedAvailable && isSaving) {
    return (
      <div className="edit-hypothesis-loader-container">
        <ResponsiveLoader dataTestId="infinite-data-loader" />
      </div>
    );
  }
  return (
    <div className="kfa-form-wrap edit-hypothesis-scroll-component">
      <Formik
        initialValues={{
          ...kfa,
          title: kfa.title || '',
          tags: kfa.tags ? kfa.tags.map((tag) => tag.text) : [],
        }}
        enableReinitialize
        onSubmit={handleSave}
        validationSchema={hypothesisSchema}
        validateOnChange
        innerRef={formRef}
      >
        {({ errors, touched, isValid, dirty, values, submitForm }) => (
          <HypothesisForm
            onDelete={handleDelete}
            onCancel={gotoHypothesisView}
            isSaving={isSaving}
            errorType={errorType}
            errors={errors}
            touched={touched}
            isValid={isValid}
            projectId={projectId}
            projectName={project.name}
            parentContentId={kfa.contentId}
            contentThreadId={kfa.contentThreadId}
            currentlyVisibleConnections={currentlyVisibleConnections}
            setCurrentlyVisibleConnections={setCurrentlyVisibleConnections}
            dirty={dirty || linkedNarrativesChanged || values.title}
            setConnectedContentChanged={setConnectedContentChanged}
            updateThumbnail={updateThumbnail}
            thumbnail={thumbnail}
            loadingThumbnail={loadingThumbnail}
            loadingItems={loadingItems}
            draftVersion={draftVersion}
            clientVersion={clientVersion}
            commentCounts={commentCounts}
            previewAction={() => previewAction(values)}
            submitForm={submitForm}
            handleRedirectToView={handleRedirectToView}
            values={values}
            isAutoSaveCalled={isAutoSaveCalled}
            setAutoSaveCalled={setAutoSaveCalled}
            isCreateMode={false}
            isSavingFulfilled={isSavingFulfilled}
            isSavingRejected={isSavingRejected}
          />
        )}
      </Formik>
    </div>
  );
};

export default EditHypothesis;
